import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, Link, useNavigate } from 'react-router-dom';
import { FaHome, FaBook, FaLayerGroup, FaUserFriends, FaChevronLeft, FaChevronRight, FaTimes, FaBars, FaChartBar, FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

const menuIcons = {
  home: FaHome,
  books: FaBook,
  series: FaLayerGroup,
  accounts: FaUserFriends,
  ets: FaChartBar
};

const Sidebar = ({ isCollapsed, onToggleCollapse }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      id: 'home',
      title: 'Home',
      path: '/',
      submenu: null
    },
    {
      id: 'books',
      title: 'Books',
      path: null,
      submenu: [
        { title: 'Overview', path: '/books/overview' },
        { title: 'Books', path: '/books/data' },
        { title: 'Published Books', path: '/books/published-data' },
        { title: 'Published Books per Day', path: '/books/published-per-day' },
        { title: 'Tags', path: '/books/tags' },
        { title: 'Downloaded Books per Day', path: '/books/downloads-per-day' },
        { title: 'Book Downloads', path: '/books/book-downloads' },
        { title: 'User Downloads', path: '/downloads/user-downloads' }
      ]
    },
    {
      id: 'series',
      title: 'Series',
      path: null,
      submenu: [
        { title: 'Overview', path: '/series/overview' },
        { title: 'Series', path: '/series/details' }
      ]
    },
    {
      id: 'accounts',
      title: 'Accounts',
      path: null,
      submenu: [
        { title: 'Overview', path: '/accounts/overview' },
        { title: 'Accounts', path: '/accounts/details' }
      ]
    },
    {
      id: 'ets',
      title: 'ETS Analytics',
      path: null,
      submenu: [
        { title: 'Overview', path: '/ets-analytics/overview' },
        { title: 'User Readings', path: '/ets-analytics/details' }
      ]
    }
  ];

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const sidebarClasses = [
    'sidebar',
    isCollapsed ? 'collapsed' : '',
    isMobileOpen ? 'mobile-open' : ''
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Mobile top bar with hamburger */}
      <div className="mobile-top-bar">
        <button className="hamburger-btn" onClick={() => setIsMobileOpen(true)}>
          <FaBars size={18} />
        </button>
        <Link to="/" className="mobile-top-bar-title">
          <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="ETS Logo" className="mobile-top-bar-logo" />
        </Link>
      </div>

      {/* Overlay for mobile drawer */}
      {isMobileOpen && (
        <div className="sidebar-overlay" onClick={() => setIsMobileOpen(false)} />
      )}

      <aside className={sidebarClasses}>
        <div className="sidebar-header">
          <Link to="/">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="ETS Logo" className="sidebar-logo" />
          </Link>
          <button className="mobile-close-btn" onClick={() => setIsMobileOpen(false)}>
            <FaTimes size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map(item => {
            const IconComponent = menuIcons[item.id];
            return (
              <div key={item.id} className="menu-item-wrapper">
                {item.submenu ? (
                  <>
                    {isCollapsed ? (
                      <NavLink
                        to={item.submenu[0].path}
                        className={({ isActive }) =>
                          `menu-item ${isActive ? 'active' : ''}`
                        }
                        title={item.title}
                      >
                        <div className="menu-icon">
                          <IconComponent size={18} />
                        </div>
                      </NavLink>
                    ) : (
                      <>
                        <div className="menu-section-label">
                          <NavLink
                            to={item.submenu[0].path}
                            className="menu-icon-link"
                            title={`Go to ${item.title} Overview`}
                          >
                            <div className="menu-icon">
                              <IconComponent size={18} />
                            </div>
                          </NavLink>
                          <span className="menu-title">{item.title}</span>
                        </div>

                        <div className="submenu show">
                          {item.submenu.map(subitem => (
                            <NavLink
                              key={subitem.path}
                              to={subitem.path}
                              className={({ isActive }) =>
                                `submenu-item ${isActive ? 'active' : ''}`
                              }
                            >
                              <span>{subitem.title}</span>
                            </NavLink>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `menu-item ${isActive ? 'active' : ''}`
                    }
                    title={isCollapsed ? item.title : ''}
                  >
                    <div className="menu-icon">
                      <IconComponent size={18} />
                    </div>
                    {!isCollapsed && <span className="menu-title">{item.title}</span>}
                  </NavLink>
                )}
              </div>
            );
          })}
        </nav>

        {/* User info and logout */}
        {user && (
          <div className="sidebar-user-section">
            {!isCollapsed && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name" title={user.email}>{user.name || user.email}</span>
              </div>
            )}
            <button
              className="sidebar-logout-btn"
              onClick={handleLogout}
              title="Sign out"
            >
              <FaSignOutAlt size={14} />
              {!isCollapsed && <span>Sign Out</span>}
            </button>
          </div>
        )}

        {/* Collapse toggle button (desktop only) */}
        <button className="collapse-toggle" onClick={onToggleCollapse}>
          <span className="collapse-icon">
            {isCollapsed ? <FaChevronRight size={14} /> : <FaChevronLeft size={14} />}
          </span>
        </button>
      </aside>
    </>
  );
};

export default Sidebar;
